import { withRouter } from "react-router"
import { connect } from "react-redux"
import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTruck } from "@fortawesome/free-solid-svg-icons/faTruck"
import { Button, Loader, Popup } from "semantic-ui-react"
import { TiresBottomNav } from "../../layouts/TiresLayout"
import { isFleetMaintenanceHubCanada } from "../../helpers/affiliationHelpers"
import { CANDA_COUNTRY_CODE, PR_COUNTRY_CODE, US_COUNTRY_CODE } from "../../constants/users"
import Geosuggest from "react-geosuggest"
import { navigationPaths } from "../../constants/paths"
import { SUCCESS } from "../../constants/tires"
import CustomNotification from "../../components/shared/CustomNotification"
import { gaJSON, trackFirebaseAnalytics } from "../../helpers/googleAnalyticsHelpers"

const ShopCard = ({ shop, setShop }) => (
  <div className="border rounded-lg p-4 bg-white" style={{ borderColor: "#E3EAE6" }}>
    <div className="flex gap-5 mb-2 text-black">
      <span className="font-medium px-2 rounded-xl text-md" style={{ backgroundColor: "#F4F7F5" }}>
        <strong>{shop.completed_shop_orders_count}</strong> CarAdvisors
      </span>
      {shop.walk_ins_only && (
        <span
          className="font-medium px-2 rounded-xl text-md"
          style={{ backgroundColor: "#F4F7F5" }}
        >
          Walk-in only
        </span>
      )}
    </div>
    <div className="flex justify-between items-end mb-4">
      <div className="flex items-center gap-3">
        <img src={shop.logo_url} alt={shop.shop_name} className="w-8 h-8" />
        <div>
          <div className="font-semibold text-lg text-black">{shop.shop_name}</div>
          <div className="text-sm text-gray-600">
            {shop.address_line1}, {shop.city}, {shop.state}, {shop.zip} -{" "}
            <span className="text-rose-700 font-bold">{Number(shop.distance).toFixed(2)} mi</span>
          </div>
        </div>
      </div>
      <div className="text-base font-semibold text-neutral-900">
        ${shop.tire_installation_price} / tire
      </div>
    </div>
    <div className="flex justify-between items-center">
      <Button
        className="text-white px-5 py-4 rounded-md transition w-full font-semibold cursor-pointer default-btn"
        onClick={() => setShop(shop)}
      >
        Select Shop
      </Button>
    </div>
  </div>
)

const SelectShop = ({ tireDetails, dispatch, history, vehicle, user }) => {
  const [shops, setShops] = useState([])
  const [loading, setLoading] = useState(false)
  const [inputValue, setInputValue] = useState(null)

  const geoSuggestRef = useRef(null)

  useEffect(() => {
    const payload = {}

    if (user.zip) {
      payload.zip = user.zip
    }

    getShops(payload)
  }, [])

  const getShops = (payload = {}) => {
    setLoading(true)
    setShops([])
    dispatch({
      type: "GET_SHOPS_FOR_TIRE_SAGA",
      payload: {
        supplier: tireDetails?.supplier,
        ...payload
      },
      callback: (res, data) => {
        if (res === SUCCESS) {
          setShops(data.shops)
        } else {
          CustomNotification("danger", "Error", data.error)
        }
        setLoading(false)
      }
    })
  }

  const setShop = (shop) => {
    trackFirebaseAnalytics({
      event: gaJSON.shop_select_clicked,
      params: {
        vehicle_id: vehicle.id,
        user_role: user.roles.toString()
      }
    })

    dispatch({
      type: "TIRES_SET_SHOP",
      payload: {
        shop
      }
    })
    history.push({
      pathname: navigationPaths.tireSchedule()
    })
  }

  const _handleSuggestSelect = (suggest) => {
    if (suggest?.location) {
      const { lat: latitude, lng: longitude } = suggest?.location
      getShops({ latitude, longitude })
    } else {
      const payload = {}
      if (user.zip) {
        payload.zip = user.zip
      }
      getShops(payload)
    }
  }

  const handleClear = () => {
    setInputValue("")
    if (geoSuggestRef.current) {
      geoSuggestRef.current.clear()
      geoSuggestRef.current.focus()
      _handleSuggestSelect(null)
    }
  }

  return (
    <>
      <div className="max-w-[670px] mx-auto mt-5 mb-[100px] select-tires-shop px-[10px]">
        <h2 className="text-lg font-semibold mb-4 text-center tires-step-header">
          Step 4 / 6: Select a shop for tire installation
        </h2>

        {/* Info Banner */}
        <div className="bg-emerald-50 text-neutral-800 text-sm rounded-md px-4 py-3 flex items-center">
          <span>
            {" "}
            <FontAwesomeIcon icon={faTruck} /> The shop you select is where your tires will be sent
            and installed. If you’d like additional services, the shop can add them when you arrive.
          </span>
        </div>

        <div className="min-h-screen flex items-start justify-center pt-5">
          <div className="w-full p-6 bg-white rounded-xl shadow">
            {/* Search Input */}
            <div className="mb-6">
              <p>Search nearby tire install shops</p>
              <div className="relative">
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Popup
                    trigger={
                      <p style={{ display: "flex", margin: "0", justifyContent: "right" }}>
                        Don't see your shop?
                      </p>
                    }
                    position="top right"
                  >
                    Only select partners offer installation of CarAdvise tires. Don’t worry - if you
                    prefer your usual shop, schedule a service appointment, choose “New Tires”, and
                    visit the shop to view their tire inventory.
                  </Popup>
                  <Geosuggest
                    ref={geoSuggestRef}
                    country={
                      isFleetMaintenanceHubCanada()
                        ? CANDA_COUNTRY_CODE
                        : [US_COUNTRY_CODE, PR_COUNTRY_CODE]
                    }
                    ignoreTab={true}
                    inputClassName="w-full rounded-xs custom-input b-0"
                    onSuggestSelect={_handleSuggestSelect}
                    placeholder={"Enter address or city"}
                    suggestItemActiveClassName="search-suggest-item-active"
                    suggestItemClassName="search-suggest-item"
                    suggestsClassName="search-suggests-list"
                    suggestsHiddenClassName="search-suggests-list-hidden"
                    types={["geocode"]}
                    style={{
                      input: {
                        height: "35px",
                        marginTop: "10px",
                        fontSize: "1em",
                        border: 0,
                        boxShadow: "0px 2px 10px 5px #0000000D"
                      }
                    }}
                    onChange={(value) => setInputValue(value)}
                  />
                  {inputValue && (
                    <button
                      onClick={handleClear}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "28px",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1em",
                        color: "#666"
                      }}
                      aria-label="Clear input"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Shop Cards */}
            {loading ? (
              <div style={{ textAlign: "center", marginTop: "40px" }}>
                <Loader active inline="centered" size="large" />
              </div>
            ) : shops.length > 0 ? (
              <div className="space-y-4">
                {shops.map((shop) => (
                  <ShopCard key={shop.id} shop={shop} setShop={setShop} />
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", marginTop: "40px" }}>
                <p>
                  No shops are available in your area right now. Please try a different location or
                  check back later.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <TiresBottomNav goBack={() => history.goBack()} showNextBtn={false} />
    </>
  )
}

function mapStateToProps(state) {
  const user = state.application.userSessionData || (state.users && state.users.currentUser)
  return {
    tireDetails: state.tires.tireDetails,
    vehicle: state.tires.vehicle,
    user: user
  }
}

export default withRouter(connect(mapStateToProps)(SelectShop))
